/**
 * Variant image preloader (Clinically Exhausted)
 *
 * After the main product image has loaded, quietly warms the browser cache
 * with the images that variant swatches jump to, one at a time, at low priority
 * and only when the browser is idle.
 *
 * - Reuses each gallery <img>'s own srcset + sizes, so the browser picks the
 *   exact same file it would load later (cache hit, no wasted bytes).
 * - Skips images already loaded, already preloaded, or repeated across variants.
 * - Does nothing on Save-Data, 2G connections, or prefers-reduced-data.
 * - Read-only: does not touch variant selection, the slideshow, or the cart.
 */
(() => {
  const MAX_PRELOADS = 12;
  const GAP_MS = 150;
  const LOG = /[?&]preload_debug=1/.test(location.search);
  // Test switch: add ?preload_off=1 to a product URL to see the "before" behaviour.
  if (/[?&]preload_off=1/.test(location.search)) return console.info('[variant-preload] off (preload_off=1)');
  const log = (...args) => LOG && console.info('[variant-preload]', ...args);

  /** Shared across re-initialisations so nothing is fetched twice. */
  const done = (window.__ceVariantPreloaded ??= new Set());

  function shouldSkipForNetwork() {
    const c = navigator.connection;
    if (c?.saveData) return 'save-data';
    if (c && /(^|-)2g$/.test(c.effectiveType || '')) return `connection ${c.effectiveType}`;
    if (window.matchMedia?.('(prefers-reduced-data: reduce)').matches) return 'prefers-reduced-data';
    return null;
  }

  const idle = (cb) =>
    'requestIdleCallback' in window ? requestIdleCallback(cb, { timeout: 3000 }) : setTimeout(cb, 300);

  function isLoaded(img) {
    return img.complete && img.naturalWidth > 0;
  }

  /** Prefer the copy of the image that is actually rendered (slideshow vs grid). */
  function findImage(gallery, mediaId) {
    const imgs = [
      ...gallery.querySelectorAll(`.product-media[data-media-id="${mediaId}"] > img.product-media__image`),
    ].filter((img) => !img.closest('dialog'));
    return imgs.find((img) => img.getClientRects().length > 0) || imgs[0] || null;
  }

  function waitForMainImage(gallery) {
    const main = gallery.querySelector('img.product-media__image[fetchpriority="high"]');
    return new Promise((resolve) => {
      if (!main || main.complete) return resolve();
      setTimeout(resolve, 8000); // safety net if load/error never fires
      main.addEventListener('load', resolve, { once: true });
      main.addEventListener('error', resolve, { once: true });
    });
  }

  function preloadOne(src) {
    return new Promise((resolve) => {
      const probe = new Image();
      probe.decoding = 'async';
      if ('fetchPriority' in probe) probe.fetchPriority = 'low';
      probe.onload = probe.onerror = () => resolve(probe.currentSrc || probe.src);
      // sizes must be set before srcset so the first candidate chosen is the right one
      if (src.sizes) probe.sizes = src.sizes;
      if (src.srcset) probe.srcset = src.srcset;
      probe.src = src.src;
    });
  }

  function readIds() {
    try {
      const el = document.getElementById('ce-variant-media-ids');
      const parsed = el ? JSON.parse(el.textContent) : [];
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  async function run(gallery, ids) {
    if (gallery.dataset.variantPreload === 'started') return;
    gallery.dataset.variantPreload = 'started';

    const skip = shouldSkipForNetwork();
    if (skip) return log('skipped:', skip);

    await waitForMainImage(gallery);

    const queue = [];
    for (const id of new Set(ids)) {
      const img = findImage(gallery, id);
      if (!img || img.getAttribute('fetchpriority') === 'high') continue; // main image: already handled by the theme
      const key = img.getAttribute('srcset') || img.getAttribute('src');
      if (!key || done.has(key)) continue;
      if (isLoaded(img)) {
        done.add(key);
        continue;
      }
      done.add(key);
      queue.push({ id, src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') });
      if (queue.length >= MAX_PRELOADS) break;
    }
    log(`queued ${queue.length} image(s)`);

    let stopped = false;
    addEventListener('pagehide', () => (stopped = true), { once: true });

    const next = () => {
      if (stopped || !queue.length) return log('finished');
      if (document.visibilityState === 'hidden') {
        return document.addEventListener('visibilitychange', () => idle(next), { once: true });
      }
      if (shouldSkipForNetwork()) return log('stopped: network changed');
      idle(async () => {
        const item = queue.shift();
        const url = await preloadOne(item);
        log('preloaded media', item.id, url);
        setTimeout(next, GAP_MS);
      });
    };

    if (document.readyState === 'complete') next();
    else addEventListener('load', next, { once: true });
  }

  function init() {
    const ids = readIds();
    if (!ids.length) return log('no variant images on this product');
    // Main product gallery only; quick-add and zoom dialogs are left alone.
    const gallery = [...document.querySelectorAll('media-gallery')].find(
      (g) => !g.closest('dialog, quick-add-dialog') && ids.some((id) => g.querySelector(`[data-media-id="${id}"]`))
    );
    if (gallery) run(gallery, ids);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
